/// <reference types="vite/client" />
/**
 * Typed API client for the admin panel.
 *
 * Wraps `openapi-fetch` with a single `Middleware` that reproduces the
 * behaviour of the legacy `static/admin/js/api.js`:
 *
 *   - injects `Authorization: Bearer <accessToken>` when a token is stored
 *   - on `401` → calls the refresh mutex (`tryRefresh`), retries once with
 *     the fresh token; on failure clears tokens, bounces via `forceLogin()`
 *     and throws `ApiError(401, ..., 'Session expired')`
 *   - on `429` → exponential backoff retry (1s → 2s → 4s, max 3 attempts)
 *   - on other 4xx/5xx → parses the JSON envelope and throws `ApiError`
 *
 * Why a single middleware instead of one per concern: `openapi-fetch` runs
 * middleware in registration order and stops the chain once a response is
 * returned. Splitting auth and retry would require careful sequencing and
 * still couple them through shared state (the cloned `Request`). One
 * middleware keeps the control flow auditable end-to-end.
 *
 * See DESIGN section 7.2.
 */
import createClient, { type Middleware } from 'openapi-fetch';
import { clearTokens, getAccessToken } from '../auth/tokens';
import { tryRefresh } from '../auth/refresh';
import { forceLogin } from '../auth/keycloak';
import type { paths } from './generated/api';
import { ApiError, type ApiErrorPayload } from './errors';

/** Backoff ladder for 429 retries (ms). The number of entries also caps
 *  retry attempts — once we exhaust the array we surface `ApiError`. */
const RETRY_DELAYS_MS: readonly number[] = [1_000, 2_000, 4_000] as const;
const MAX_RETRIES = RETRY_DELAYS_MS.length;

const baseUrl = import.meta.env.VITE_API_BASE ?? '';

/** Promise-based delay helper. Pure so tests can stub `setTimeout`. */
const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Best-effort decode of an error response body. Spring returns JSON for
 * every error, but middleware boxes (nginx, ELB) may inject HTML 5xx pages
 * that fail JSON parsing — hence the try/catch fallback to `statusText`.
 */
const parseErrorPayload = async (response: Response): Promise<ApiErrorPayload> => {
  try {
    const body = (await response.clone().json()) as unknown;
    if (typeof body === 'object' && body !== null) {
      return body as ApiErrorPayload;
    }
  } catch {
    /* fall through to statusText */
  }
  return { message: response.statusText };
};

/**
 * Throws an `ApiError` describing the failed `response`. Always rejects —
 * the `never` return type lets call sites use it inside expression
 * positions without TS assuming the surrounding control flow continues.
 */
const throwApiError = async (response: Response): Promise<never> => {
  const payload = await parseErrorPayload(response);
  throw new ApiError(response.status, payload, `HTTP ${response.status}`);
};

/**
 * Pristine request clones captured *before* the body is consumed, so a
 * 401-refresh or 429-backoff can replay POST/PUT bodies. A `Request` body is
 * a one-shot stream: cloning it *after* the initial fetch (the old bug) threw
 * "Failed to execute 'clone' on 'Request': Request body is already used",
 * silently failing every write whose access token expired mid-session
 * (e.g. the catalog `.xlsx` import loop). Keyed by the request instance
 * openapi-fetch threads through `onRequest` → `onResponse`; a WeakMap lets
 * entries GC once the call settles.
 */
const pristineRequests = new WeakMap<Request, Request>();

/**
 * Sends `request` after applying the *current* access token. We rebuild
 * the headers on every retry because the token may have rotated since the
 * original call (the whole point of the 401 → refresh → retry path).
 */
const sendWithAuth = async (request: Request): Promise<Response> => {
  const next = request.clone();
  const token = getAccessToken();
  if (token) {
    next.headers.set('Authorization', `Bearer ${token}`);
  } else {
    next.headers.delete('Authorization');
  }
  return fetch(next);
};

/**
 * Handles a 401 by trying to refresh the access token and replaying the
 * original request once. Returns the replayed response on success.
 *
 * On refresh failure, clears local tokens, bounces to login via `forceLogin()`,
 * and throws `ApiError(401, ..., 'Session expired')` so callers (TanStack
 * Query, React Hook Form) observe a structured error rather than a silent
 * navigation. Never returns `undefined` — either a fresh `Response` or
 * throws.
 */
const handleUnauthorized = async (request: Request): Promise<Response> => {
  const refreshed = await tryRefresh();
  if (refreshed) {
    return sendWithAuth(request);
  }

  clearTokens();
  forceLogin(); // OIDC → kc.login(); sem instância KC → raiz (PanelGuard assume).

  throw new ApiError(401, { message: 'Session expired' }, 'Session expired');
};

/**
 * Retries a 429 response with exponential backoff. The first call already
 * counts as attempt #1 — `attempt` here is the *next* attempt index, so
 * `attempt === MAX_RETRIES` means we have already issued 3 retries on top
 * of the original request (4 network calls total) and should surface the
 * latest 429 to the caller.
 *
 * Stops early when the server returns a non-429 (success or another
 * error) — that response is returned verbatim and the surrounding
 * middleware decides what to do with it.
 */
const handleTooManyRequests = async (
  request: Request,
  initialResponse: Response
): Promise<Response> => {
  let response = initialResponse;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt += 1) {
    const delay = RETRY_DELAYS_MS[attempt] ?? 0;
    await sleep(delay);
    response = await sendWithAuth(request);
    if (response.status !== 429) return response;
  }
  return response;
};

/**
 * `onRequest` is intentionally minimal: the auth header is the only piece
 * of state we need to pin onto every outgoing request. All retry logic
 * lives in `onResponse` where we have the status code to branch on.
 */
const attachAuthHeader = (request: Request): void => {
  const token = getAccessToken();
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
  }
};

/**
 * Resolves the final `Response` after applying retry / refresh policies.
 * Centralising the branch logic here keeps `onResponse` shallow and below
 * the cyclomatic-complexity budget.
 */
const resolveResponse = async (request: Request, response: Response): Promise<Response> => {
  // Replay from the pristine clone captured in `onRequest`: the `request`
  // handed to `onResponse` has already had its body consumed by the initial
  // send, so cloning it again for a retry would throw "body is already used".
  const replayable = pristineRequests.get(request) ?? request;
  if (response.status === 401) {
    return handleUnauthorized(replayable);
  }
  if (response.status === 429) {
    return handleTooManyRequests(replayable, response);
  }
  return response;
};

const authRetryMiddleware: Middleware = {
  onRequest({ request }) {
    attachAuthHeader(request);
    // Stash a body-intact clone now, while the stream is still readable, so
    // `resolveResponse` can replay this request after a 401/429 (a Request
    // body cannot be cloned once consumed). Bodyless GETs clone trivially.
    try {
      pristineRequests.set(request, request.clone());
    } catch {
      /* nothing to replay */
    }
    // Returning `undefined` keeps openapi-fetch's original request — we
    // mutated headers in-place which is the documented pattern.
    return undefined;
  },
  async onResponse({ request, response }) {
    const finalResponse = await resolveResponse(request, response);

    // Surface non-2xx responses as structured `ApiError`s so TanStack
    // Query's `error` channel carries server context (code/details) and
    // not just an opaque string. 2xx (and 204) flow through untouched —
    // openapi-fetch handles `data` parsing.
    if (!finalResponse.ok) {
      await throwApiError(finalResponse);
    }
    return finalResponse;
  },
};

/**
 * Singleton typed API client. Feature hooks import `api` and call e.g.
 * `api.GET('/api/v1/admin/orders', { params: { query: filters } })`.
 *
 * `baseUrl` is read from `VITE_API_BASE`; in dev the empty default plus
 * the Vite proxy makes calls hit `http://localhost:8080` transparently.
 */
export const api = createClient<paths>({ baseUrl });
api.use(authRetryMiddleware);
